import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertCircle, Check, FileText, Download, Clock, Send, Mail, Smartphone, Shield, Globe, Euro } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
];

const CONTRACT_STATUS = {
  pending: { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
  signed: { label: 'Signé', color: 'bg-green-500', icon: Check },
};

export default function ProfessionalContract() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Signature flow states
  const [signatureStep, setSignatureStep] = useState('request'); // request, verify_sms, verify_email, sign
  const [smsOtp, setSmsOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [otpData, setOtpData] = useState(null);
  const [smsVerified, setSmsVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [signing, setSigning] = useState(false);
  const [simpleSignatureAccepted, setSimpleSignatureAccepted] = useState(false);
  
  // Contract generation
  const [newContractLanguage, setNewContractLanguage] = useState('fr');
  const [negotiatedPrice, setNegotiatedPrice] = useState('');
  const [vatPreview, setVatPreview] = useState(null);

  const fetchContracts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data.contracts || []);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchContracts();
    }
  }, [token, fetchContracts]);

  // Auto-generate contract if none exists
  useEffect(() => {
    const autoGenerate = async () => {
      if (!loading && contracts.length === 0 && token && user?.id && !generatingContract) {
        setGeneratingContract(true);
        try {
          const lang = user?.language || 'fr';
          const response = await fetch(`${API_URL}/api/professional-contracts/generate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              professional_id: user.id,
              language: lang === 'fr' || lang === 'en' ? lang : 'fr',
              negotiated_price: null
            })
          });
          if (response.ok) {
            await fetchContracts();
          }
        } catch (err) {
          console.error('Auto-generate contract error:', err);
        } finally {
          setGeneratingContract(false);
        }
      }
    };
    autoGenerate();
  }, [loading, contracts.length, token, user?.id]);

  // Calculate VAT preview
  useEffect(() => {
    const calculateVat = async () => {
      if (!user?.country_code) return;
      const price = negotiatedPrice ? parseFloat(negotiatedPrice) : 150;
      try {
        const response = await fetch(
          `${API_URL}/api/professional-contracts/vat/calculate?price=${price}&country_code=${user.country_code || 'FR'}`
        );
        if (response.ok) {
          setVatPreview(await response.json());
        }
      } catch (error) {
        console.error('VAT calculation error:', error);
      }
    };
    calculateVat();
  }, [negotiatedPrice, user?.country_code]);

  const generateContract = async () => {
    setGeneratingContract(true);
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          professional_id: user.id,
          language: newContractLanguage,
          negotiated_price: negotiatedPrice ? parseFloat(negotiatedPrice) : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Contrat généré avec succès');
        fetchContracts();
        setSelectedContract(data);
        setShowPreviewModal(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setGeneratingContract(false);
    }
  };

  const requestOtps = async (contractId) => {
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/request-otps/${contractId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOtpData(data);
        setSignatureStep('verify_sms');
        toast.success('Codes de vérification envoyés');
        
        // For testing, show the OTPs in console (remove in production)
        console.log('Test OTPs:', { sms: data.sms_otp, email: data.email_otp });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const verifySmsOtp = async () => {
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/verify-sms-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contract_id: selectedContract.id || selectedContract.contract_id,
          otp: smsOtp
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSmsVerified(true);
        toast.success('Code SMS vérifié');
        setSignatureStep('verify_email');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Code SMS invalide');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const verifyEmailOtp = async () => {
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/verify-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contract_id: selectedContract.id || selectedContract.contract_id,
          otp: emailOtp
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailVerified(true);
        toast.success('Code Email vérifié');
        if (data.both_verified) {
          setSignatureStep('sign');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Code Email invalide');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const signContract = async () => {
    setSigning(true);
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contract_id: selectedContract.id || selectedContract.contract_id
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Contrat signé avec succès!');
        setShowSignatureModal(false);
        resetSignatureFlow();
        fetchContracts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la signature');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSigning(false);
    }
  };

  const downloadPdf = async (contractId) => {
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/${contractId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contract_${contractId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF téléchargé');
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const resetSignatureFlow = () => {
    setSignatureStep('request');
    setSmsOtp('');
    setEmailOtp('');
    setOtpData(null);
    setSmsVerified(false);
    setEmailVerified(false);
    setSelectedContract(null);
    setSimpleSignatureAccepted(false);
  };

  // Simple signature (checkbox + date/IP)
  const signContractSimple = async () => {
    if (!simpleSignatureAccepted) {
      toast.error('Veuillez accepter les conditions');
      return;
    }
    
    setSigning(true);
    try {
      const response = await fetch(`${API_URL}/api/professional-contracts/sign-simple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contract_id: selectedContract.id || selectedContract.contract_id,
          accepted_terms: true,
          signature_date: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        toast.success('Contrat signé avec succès!');
        setShowSignatureModal(false);
        resetSignatureFlow();
        fetchContracts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la signature');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSigning(false);
    }
  };

  const startSignature = (contract) => {
    setSelectedContract(contract);
    resetSignatureFlow();
    setShowSignatureModal(true);
  };

  const hasSignedContract = contracts.some(c => c.status === 'signed');
  const pendingContract = contracts.find(c => c.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <FileText className="h-8 w-8 text-blue-600" />
            Contrat Professionnel
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gérez votre contrat d'abonnement drbooking
          </p>
        </div>
        
        {hasSignedContract && (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-4 py-2">
            <Check className="h-4 w-4 mr-2" />
            Contrat actif
          </Badge>
        )}
      </div>

      {/* Contract Status Card */}
      {hasSignedContract ? (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-300 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Contrat signé et actif
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contracts.filter(c => c.status === 'signed').map(contract => (
              <div key={contract.id} className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Langue</span>
                    <p className="font-medium">
                      {LANGUAGES.find(l => l.code === contract.language)?.flag} {contract.language.toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Prix HT</span>
                    <p className="font-medium">{contract.price_excl_vat?.toFixed(2)} €</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Prix TTC</span>
                    <p className="font-medium">{contract.price_incl_vat?.toFixed(2)} €</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Signé le</span>
                    <p className="font-medium">
                      {contract.signed_at ? new Date(contract.signed_at).toLocaleDateString('fr-FR') : '-'}
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => downloadPdf(contract.id)}
                  className="mt-4"
                  data-testid="download-contract-pdf"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger le contrat PDF
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : pendingContract ? (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardHeader>
            <CardTitle className="text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Contrat en attente de signature
            </CardTitle>
            <CardDescription>
              Vous avez un contrat généré en attente de votre signature électronique
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-sm text-gray-500">Langue</span>
                <p className="font-medium">
                  {LANGUAGES.find(l => l.code === pendingContract.language)?.flag} {pendingContract.language.toUpperCase()}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Prix HT</span>
                <p className="font-medium">{pendingContract.price_excl_vat?.toFixed(2)} €</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">TVA ({pendingContract.vat_rate}%)</span>
                <p className="font-medium">{pendingContract.vat_amount?.toFixed(2)} €</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Prix TTC</span>
                <p className="font-medium text-blue-600">{pendingContract.price_incl_vat?.toFixed(2)} €</p>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => startSignature(pendingContract)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="sign-contract-btn"
              >
                <Shield className="h-4 w-4 mr-2" />
                Signer le contrat
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedContract(pendingContract);
                  setShowPreviewModal(true);
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Aperçu
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Générer un nouveau contrat
            </CardTitle>
            <CardDescription>
              Créez votre contrat d'abonnement professionnel drbooking
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="language">
                  <Globe className="h-4 w-4 inline mr-2" />
                  Langue du contrat
                </Label>
                <Select value={newContractLanguage} onValueChange={setNewContractLanguage}>
                  <SelectTrigger data-testid="contract-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price">
                  <Euro className="h-4 w-4 inline mr-2" />
                  Prix mensuel HT (optionnel)
                </Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="200 (par défaut)"
                  value={negotiatedPrice}
                  onChange={(e) => setNegotiatedPrice(e.target.value)}
                  data-testid="contract-price-input"
                />
                <p className="text-xs text-gray-500">
                  Laissez vide pour le prix standard de 150€ HT/mois
                </p>
              </div>
            </div>
            
            {vatPreview && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                  Aperçu du tarif ({vatPreview.country_name_fr})
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">HT</span>
                    <p className="font-semibold">{vatPreview.price_excl_vat?.toFixed(2)} €</p>
                  </div>
                  <div>
                    <span className="text-gray-500">TVA ({vatPreview.vat_rate}%)</span>
                    <p className="font-semibold">{vatPreview.vat_amount?.toFixed(2)} €</p>
                  </div>
                  <div>
                    <span className="text-gray-500">TTC</span>
                    <p className="font-semibold text-blue-600">{vatPreview.price_incl_vat?.toFixed(2)} €</p>
                  </div>
                </div>
              </div>
            )}
            
            <Button
              onClick={generateContract}
              disabled={generatingContract}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="generate-contract-btn"
            >
              {generatingContract ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Génération...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Générer le contrat
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Contracts History */}
      {contracts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Historique des contrats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {contracts.map(contract => {
                const status = CONTRACT_STATUS[contract.status] || CONTRACT_STATUS.pending;
                const StatusIcon = status.icon;
                
                return (
                  <div
                    key={contract.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${status.color}`}>
                        <StatusIcon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {LANGUAGES.find(l => l.code === contract.language)?.flag} Contrat {contract.language.toUpperCase()}
                        </p>
                        <p className="text-sm text-gray-500">
                          Créé le {new Date(contract.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={contract.status === 'signed' ? 'success' : 'secondary'}>
                        {status.label}
                      </Badge>
                      <span className="font-medium">{contract.price_incl_vat?.toFixed(2)} €</span>
                      {contract.status === 'signed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPdf(contract.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature Modal */}
      <Dialog open={showSignatureModal} onOpenChange={(open) => {
        if (!open) resetSignatureFlow();
        setShowSignatureModal(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Signature électronique
            </DialogTitle>
            <DialogDescription>
              Choisissez votre méthode de signature
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {signatureStep === 'request' && (
              <div className="space-y-6">
                {/* Option 1: Simple Signature */}
                <div className="border-2 border-green-200 bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <Check className="h-5 w-5" />
                    Signature simple (Recommandée)
                  </h4>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={simpleSignatureAccepted}
                        onChange={(e) => setSimpleSignatureAccepted(e.target.checked)}
                        className="mt-1 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        data-testid="simple-signature-checkbox"
                      />
                      <span className="text-sm text-gray-700">
                        Je certifie avoir lu et accepté les termes du contrat, les CGU et la politique de confidentialité RGPD. 
                        Je comprends que cette signature électronique a la même valeur juridique qu'une signature manuscrite.
                      </span>
                    </label>
                    <div className="text-xs text-gray-500 bg-white p-2 rounded">
                      <p><strong>Date :</strong> {new Date().toLocaleString('fr-FR')}</p>
                      <p><strong>Email :</strong> {user?.email}</p>
                    </div>
                    <Button
                      onClick={signContractSimple}
                      disabled={!simpleSignatureAccepted || signing}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="simple-sign-btn"
                    >
                      {signing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Signature en cours...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Signer maintenant
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Separator */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-sm text-gray-500">ou</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                {/* Option 2: SMS + Email Verification */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Signature renforcée (SMS + Email)
                  </h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Vérification en deux étapes avec codes envoyés par SMS et Email
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => requestOtps(selectedContract?.id || selectedContract?.contract_id)}
                    className="w-full"
                    data-testid="request-otps-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Utiliser la vérification SMS + Email
                  </Button>
                </div>
              </div>
            )}

            {/* SMS Verification Step */}
            {signatureStep === 'verify_sms' && (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <div className="w-8 h-0.5 bg-green-500" />
                  <div className="w-3 h-3 rounded-full bg-blue-600" />
                  <div className="w-8 h-0.5 bg-gray-300" />
                  <div className="w-3 h-3 rounded-full bg-gray-300" />
                </div>
                <div className="text-center">
                  <Smartphone className="h-12 w-12 mx-auto text-blue-600 mb-2" />
                  <p className="font-medium">Vérification SMS</p>
                  <p className="text-sm text-gray-500">
                    Code envoyé au {otpData?.phone_masked}
                  </p>
                </div>
                <Input
                  type="text"
                  placeholder="Entrez le code à 6 chiffres"
                  value={smsOtp}
                  onChange={(e) => setSmsOtp(e.target.value)}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                  data-testid="sms-otp-input"
                />
                <Button onClick={verifySmsOtp} className="w-full" data-testid="verify-sms-btn">
                  Vérifier le code SMS
                </Button>
              </div>
            )}

            {signatureStep === 'verify_email' && (
              <div className="space-y-4">
                <div className="text-center">
                  <Mail className="h-12 w-12 mx-auto text-blue-600 mb-2" />
                  <p className="font-medium">Vérification Email</p>
                  <p className="text-sm text-gray-500">
                    Code envoyé à {otpData?.email_masked}
                  </p>
                </div>
                <Input
                  type="text"
                  placeholder="Entrez le code à 8 caractères"
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.toUpperCase())}
                  maxLength={8}
                  className="text-center text-2xl tracking-widest"
                  data-testid="email-otp-input"
                />
                <Button onClick={verifyEmailOtp} className="w-full" data-testid="verify-email-btn">
                  Vérifier le code Email
                </Button>
                
                <div className="flex items-center gap-2 text-green-600 text-sm">
                  <Check className="h-4 w-4" />
                  SMS vérifié
                </div>
              </div>
            )}

            {signatureStep === 'sign' && (
              <div className="space-y-4 text-center">
                <div className="flex items-center justify-center gap-4 text-green-600">
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    <span className="text-sm">SMS vérifié</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    <span className="text-sm">Email vérifié</span>
                  </div>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <Shield className="h-12 w-12 mx-auto text-green-600 mb-2" />
                  <p className="font-medium text-green-700 dark:text-green-300">
                    Prêt à signer
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    En cliquant sur "Signer", vous acceptez les termes du contrat, 
                    les CGU et le consentement RGPD.
                  </p>
                </div>
                
                <Button
                  onClick={signContract}
                  disabled={signing}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="final-sign-btn"
                >
                  {signing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signature en cours...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Signer le contrat
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu du contrat</DialogTitle>
          </DialogHeader>
          {selectedContract?.content ? (
            <div className="bg-gray-50 p-6 rounded-lg text-sm whitespace-pre-wrap overflow-x-auto leading-relaxed" style={{fontFamily: 'Georgia, serif'}}>
              {selectedContract.content}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Contenu du contrat en cours de chargement...</p>
              <p className="text-sm mt-2">Vous pouvez télécharger le PDF ci-dessous.</p>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Fermer
            </Button>
            {selectedContract && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  const contractId = selectedContract.id || selectedContract.contract_id;
                  window.open(`${API_URL}/api/professional-contracts/${contractId}/pdf?token=${token}`, '_blank');
                }}
                data-testid="download-contract-pdf"
              >
                <Download className="h-4 w-4" />
                Télécharger le PDF
              </Button>
            )}
            {selectedContract?.status === 'pending' && (
              <Button onClick={() => {
                setShowPreviewModal(false);
                startSignature(selectedContract);
              }}>
                <Shield className="h-4 w-4 mr-2" />
                Procéder à la signature
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
